import { Zap, Award, ShieldCheck, User } from "lucide-react";

const TYPE_LABELS = { free_evaluation: "Free Evaluation", in_person: "In-Person", online: "Online Coaching", hybrid: "Hybrid", digital: "Online Coaching" };
const STATUS_LABELS = {
  active: "Active", pending: "Pending", trial: "Trial", complimentary: "Complimentary",
  scholarship: "Scholarship", paused: "Paused", past_due: "Past Due", canceled: "Canceled", manual: "Active",
};

const Ring = ({ pct }) => {
  const r = 26, c = 2 * Math.PI * r, off = c - (pct / 100) * c;
  return (
    <div className="relative shrink-0" style={{ width: 64, height: 64 }}>
      <svg width="64" height="64" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="5" />
        <circle cx="32" cy="32" r={r} fill="none" stroke="#fff" strokeWidth="5" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 32 32)" style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-bold text-white text-sm">{pct}%</span>
    </div>
  );
};

export default function DashboardHeader({ dash }) {
  if (!dash) return null;
  const a = dash.athlete || {};
  const lvl = dash.level || {};
  const sp = dash.speed_points || {};
  const mem = dash.membership;
  const badge = dash.latest_achievement;
  const status = mem?.status || dash.account?.status || a.status || "active";

  return (
    <div className="bg-[#121212] border border-white/10 mb-8" data-testid="athlete-dash-header">
      <div className="p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
        {/* Avatar */}
        <div className="w-20 h-20 rounded-full overflow-hidden bg-[#1a1a1a] border border-white/15 flex items-center justify-center shrink-0">
          {a.photo_url ? (
            <img src={a.photo_url} alt={a.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : <User size={34} className="text-white/30" />}
        </div>
        {/* Identity */}
        <div className="flex-1 min-w-0">
          <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1">Welcome back</p>
          <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl leading-none truncate" data-testid="athlete-welcome">{a.first_name || a.name || "Athlete"}</h1>
          <div className="flex flex-wrap gap-2 mt-3">
            <span data-testid="header-athlete-type" className="font-mono-accent text-[10px] tracking-[0.15em] uppercase border border-white/25 px-2.5 py-1 text-white/70">{TYPE_LABELS[a.athlete_type] || a.athlete_type}</span>
            <span data-testid="header-membership" className="font-mono-accent text-[10px] tracking-[0.15em] uppercase border border-white/25 px-2.5 py-1 text-white/70">{mem?.name || "No Membership"}</span>
            <span data-testid="header-account-status" className={`font-mono-accent text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 border ${status === "active" || status === "manual" ? "bg-white text-black border-white" : "text-white border-white/40"}`}>{STATUS_LABELS[status] || status}</span>
          </div>
        </div>
        {/* Profile completion ring */}
        <div className="flex items-center gap-3 shrink-0" data-testid="header-profile-completion">
          <Ring pct={dash.profile_completion?.pct ?? 0} />
          <div><p className="text-white/40 text-[10px] uppercase tracking-widest">Profile</p><p className="font-bold text-sm">Complete</p></div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border-t border-white/10">
        <div className="bg-[#0A0A0A] p-4 flex items-center gap-3" data-testid="header-speed-points">
          <Zap size={18} className="text-white/50 shrink-0" />
          <div><p className="font-display font-black text-2xl leading-none">{sp.lifetime ?? 0}</p><p className="text-white/40 text-[10px] uppercase tracking-widest mt-1">Speed Points</p></div>
        </div>
        <div className="bg-[#0A0A0A] p-4" data-testid="header-level">
          <div className="flex items-center gap-2 mb-1.5"><ShieldCheck size={16} className="text-white/50" /><p className="font-display font-black uppercase tracking-tight text-lg leading-none">{lvl.name || "Rookie"}</p></div>
          <div className="w-full h-1.5 bg-white/10"><div className="h-full bg-white" style={{ width: `${lvl.progress_pct ?? 0}%` }} /></div>
          <p className="text-white/40 text-[10px] uppercase tracking-widest mt-1.5">{lvl.next_name ? `${lvl.sp_to_next} SP to ${lvl.next_name}` : "Max level reached"}</p>
        </div>
        <div className="bg-[#0A0A0A] p-4 flex items-center gap-3 col-span-2 md:col-span-1" data-testid="header-latest-badge">
          <Award size={18} className="text-white/50 shrink-0" />
          <div className="min-w-0"><p className="font-bold text-sm truncate">{badge?.title || "No badges yet"}</p><p className="text-white/40 text-[10px] uppercase tracking-widest mt-1">Latest Achievement</p></div>
        </div>
      </div>
    </div>
  );
}
